import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "react-axios";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Toggle from "react-toggle";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";

import { useUser } from "~/Context/userContext";
import { getShuffleRandomTrack } from "~/api/getShuffle";
import { getTrackReplies, getNumberOfTrackReplies } from "~/api/getTrack";

import TrackHeader from "~/Pages/Track/TrackHeader";
import TrackDetails from "~/Pages/Track/TrackDetails";
import TrackGenre from "~/Components/Elements/TrackGenre";
import ChimneyName from "~/Components/Elements/ChimneyName";
import TrackFooter from "~/Pages/Track/TrackFooter";
import TrackReruleDetails from "~/Pages/Track/TrackReruleDetails";
import TrackCommentsForm from "~/Components/Elements/Forms/TrackCommentsForm";
import TrackComment from "~/Components/Elements/TrackComment";
import LoadMore from "~/Components/Elements/LoadMore";

import { COMMENTS_PER_PAGE } from "~/totdConfig";

import "react-toggle/style.css";
import "./ShufflePage.less";
import AdditionalFileUnder from "~/Components/Elements/AdditionalFileUnder";
import { AdditionalGenresProvider } from "~/Context/AdditionalGenresContext";

const SHUFFLE_HISTORY_KEY = "dreamchimney_shuffle_history";
const SHUFFLE_HISTORY_MAX = 8;

const loadShuffleHistory = () => {
  try {
    const raw = localStorage.getItem(SHUFFLE_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, SHUFFLE_HISTORY_MAX) : [];
  } catch (e) {
    return [];
  }
};

const saveShuffleHistory = (list) => {
  try {
    localStorage.setItem(SHUFFLE_HISTORY_KEY, JSON.stringify(list.slice(0, SHUFFLE_HISTORY_MAX)));
  } catch (e) { }
};

const ShufflePage = () => {
  const { state: userState } = useUser();
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onlyMyTracks, setOnlyMyTracks] = useState(false);
  const [previousTracks, setPreviousTracks] = useState(loadShuffleHistory);
  const [hasShuffledThisSession, setHasShuffledThisSession] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsPage, setCommentsPage] = useState(1);
  const [commentsHasMore, setCommentsHasMore] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentCount, setCommentCount] = useState(null);
  const hasAutoShuffledRef = useRef(false);

  const isChimney = !!(userState.loggedin && userState.isChim);
  const hasTrack = track && !loading;

  const trackId = track && track.id;
  useEffect(() => {
    if (!trackId) return;
    setShowAllComments(false);
    setComments([]);
    setCommentsPage(1);
    setCommentsHasMore(false);
    setCommentCount(null);
    getNumberOfTrackReplies(axios, trackId).then(({ data }) => {
      const count = data && data.length ? parseInt(data[0], 10) : 0;
      setCommentCount(count);
    }).catch(() => setCommentCount(0));
  }, [trackId]);

  const loadComments = useCallback((page = 1, append = false) => {
    if (!track || !track.id) return;
    setCommentsLoading(true);
    getTrackReplies(axios, track.id, page).then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setComments((prev) => (append ? prev.concat(list) : list));
      setCommentsHasMore(list.length === COMMENTS_PER_PAGE);
      setCommentsPage(page + 1);
      setCommentsLoading(false);
    }).catch(() => setCommentsLoading(false));
  }, [track]);

  const handleShowAllComments = useCallback(() => {
    setShowAllComments(true);
    if (comments.length === 0 && !commentsLoading) loadComments(1, false);
  }, [track, comments.length, commentsLoading, loadComments]);

  const handleLoadMoreComments = useCallback((e) => {
    e.stopPropagation();
    loadComments(commentsPage, true);
  }, [loadComments, commentsPage]);

  const handleCommentReply = useCallback((replies) => {
    const list = Array.isArray(replies) ? replies : [];
    setComments(list);
    setCommentCount(list.length);
    setShowAllComments(true);
    setCommentsHasMore(false);
  }, []);

  const handleShuffle = useCallback(() => {
    setError(null);
    setLoading(true);
    const options = { onlyMyTracks };
    if (onlyMyTracks && userState.user && userState.user.dir) {
      options.chimdir = userState.user.dir;
    }
    getShuffleRandomTrack(axios, options)
      .then((data) => {
        setTrack(data);
        setHasShuffledThisSession(true);
        const entry = { id: data.id, title: data.title, artist: data.artist };
        setPreviousTracks((prev) => {
          const next = [entry].concat(prev.filter((t) => t.id !== data.id)).slice(0, SHUFFLE_HISTORY_MAX);
          saveShuffleHistory(next);
          return next;
        });
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        const msg = err.response?.status === 401
          ? "Please log in again to use “Only my tracks”."
          : err.response?.data?.error || "Could not load a track. Try again.";
        setError(msg);
        if (err.response?.status !== 401) setTrack(null);
      });
  }, [onlyMyTracks, userState.user]);

  useEffect(() => {
    if (hasAutoShuffledRef.current) return;
    if (loadShuffleHistory().length === 0) return;
    hasAutoShuffledRef.current = true;
    handleShuffle();
  }, [handleShuffle]);

  const showRightPanel = hasTrack || loading;
  const previousTracksToShow = trackId
    ? previousTracks.filter((t) => t.id !== trackId)
    : previousTracks;

  return (
    <div className={"ShufflePage page-layout-topic" + (showRightPanel ? " ShufflePage--has-track" : "")}>
      <div className="home-header">
        <h2>Track Shuffle</h2>
        {/* <p className="shuffle-page-subtitle">Hit shuffle and get a random track from the archives</p> */}
      </div>
      {/* <header className="shuffle-page-header">
        <h1 className="shuffle-page-title">Track Shuffle</h1>
        <p className="shuffle-page-subtitle">Hit shuffle and get a random track from the archives</p>
      </header> */}

      {error && (
        <div className="shuffle-error" role="alert">
          {error}
        </div>
      )}

      <div className="shuffle-layout">
        <aside className="shuffle-sidebar">
          <div className="shuffle-controls">
            <button
              type="button"
              className="shuffle-button"
              onClick={handleShuffle}
              disabled={loading}
              title="Pick a random track"
            >
              <FontAwesomeIcon icon="random" className="shuffle-icon" />
              {loading ? "..." : "Shuffle"}
            </button>
            {isChimney && (
              <div className="shuffle-toggle-row">
                <label htmlFor="shuffle-only-my-tracks">
                  <Toggle
                    id="shuffle-only-my-tracks"
                    checked={onlyMyTracks}
                    icons={false}
                    onChange={(e) => setOnlyMyTracks(e.target.checked)}
                  />
                </label>
                <label className="shuffle-toggle-label" htmlFor="shuffle-only-my-tracks">
                  Only my tracks
                </label>
              </div>
            )}
            {!hasShuffledThisSession && <div style={{ marginTop: 20 }}>Click the shuffle button to get a random track from the archives</div>}
          </div>
          {hasShuffledThisSession && previousTracksToShow.length > 0 && (
            <div className="shuffle-previous">
              <h3 className="shuffle-previous-title">Previously shuffled</h3>
              <ul className="shuffle-previous-list">
                {previousTracksToShow.map((t) => (
                  <li key={t.id}>
                    <Link to={"/" + t.id}>{t.title} — {t.artist}</Link>
                  </li>
                ))}
              </ul>
              <button type="button" className="shuffle-clear-history" onClick={() => { setPreviousTracks([]); saveShuffleHistory([]); }}>
                Clear history
              </button>
            </div>
          )}
        </aside>

        {showRightPanel && (
          <div className="shuffle-track-panel">
            {loading ? (
              <div className="shuffle-track-skeleton">
                <SkeletonTheme color="#eee" highlightColor="#f5f5f5">
                  <Skeleton height={16} width="60%" style={{ marginBottom: 16 }} />
                  <Skeleton height={28} width="85%" style={{ marginBottom: 12 }} />
                  <Skeleton height={18} width="40%" style={{ marginBottom: 20 }} />
                  <Skeleton height={120} style={{ marginBottom: 16 }} />
                  <Skeleton height={18} width="70%" style={{ marginBottom: 8 }} />
                  <Skeleton height={18} width="50%" style={{ marginBottom: 20 }} />
                  <Skeleton height={48} width="100%" style={{ marginBottom: 16 }} />
                  <Skeleton height={60} style={{ marginBottom: 12 }} />
                  <Skeleton height={60} style={{ marginBottom: 12 }} />
                  <Skeleton height={40} width="80%" />
                </SkeletonTheme>
              </div>
            ) : (
              <>
                <p className="shuffle-track-posted-date">Originally posted by <ChimneyName user={track.user} /> on {track.date} </p>
                <div className="track-topic-box">
                  <AdditionalGenresProvider trackId={track.id}>
                    <TrackHeader track={track} artistLink trackLink={false} />
                    <Row>
                      <Col lg={12} xs={12}>
                        <div id="track-info-wrapper">
                          <TrackGenre track={track} label />
                        </div>
                        <TrackDetails loading={false} track={track} />
                        <TrackReruleDetails loading={false} track={track} />
                        <TrackFooter showTaggingButton track={track} />
                        <AdditionalFileUnder trackId={track.id} />

                        <div className="shuffle-comments-section">
                        <p className="shuffle-comments-add">Add a comment...</p>
                        <TrackCommentsForm track={track} onReply={handleCommentReply} />
                        {commentCount > 0 && (
                          <div className="shuffle-show-comments-wrap text-center">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="shuffle-show-comments-btn"
                              onClick={handleShowAllComments}
                            >
                              Show all comments
                            </Button>
                          </div>
                        )}
                        {showAllComments && (
                          <div className="shuffle-comments-list">
                            {commentsLoading && comments.length === 0 && (
                              <div className="shuffle-comments-loading">
                                <Spinner animation="border" size="sm" /> Loading...
                              </div>
                            )}
                            {!commentsLoading && comments.length > 0 && (
                              <>
                                <div className="totd-header">
                                  <h3>
                                    Comments for &ldquo;<span>{track.title}</span>&rdquo; by <span>{track.artist}</span>
                                  </h3>
                                </div>
                                {comments.map((comment) => (
                                  <div key={comment.id}>
                                    <TrackComment comment={comment} type="comment" highlight={track.id} />
                                  </div>
                                ))}
                                <LoadMore
                                  loading={commentsLoading}
                                  hasMore={commentsHasMore}
                                  items={comments}
                                  pagination={handleLoadMoreComments}
                                />
                              </>
                            )}
                            {!commentsLoading && showAllComments && comments.length === 0 && (
                              <div className="shuffle-comments-empty">No comments yet.</div>
                            )}
                          </div>
                        )}
                        </div>
                      </Col>
                    </Row>
                  </AdditionalGenresProvider>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShufflePage;
